import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Images from '../../assests/images/Images';
import CtaButton from '../../components/common/CtaButton';

/* ─── Scroll Reveal Hook (Same smooth viewport transition as Home) ─── */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    // Scroll listener fallback check
    const checkScrollPosition = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) {
          el.classList.add('sipl-revealed');
        }
      });
    };

    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      observer.disconnect();
    };
  }, []);
};

/* ─── Data Arrays (Human-Refined Copy) ─── */


const STORY_STEPS = [
  {
    num: '01', badgeClass: 'sipl-bg-navy',
    title: 'The Challenge',
    desc: 'Off-the-shelf software often forces companies to adapt to rigid features rather than supporting their actual business workflow.',
  },
  {
    num: '02', badgeClass: 'sipl-bg-teal',
    title: 'Our Approach',
    desc: 'We founded Seikural Innovation to bridge the gap between technical engineering and operational strategy—building software around real needs.',
  },
  {
    num: '03', badgeClass: 'sipl-bg-green',
    title: 'Practical Engineering',
    desc: 'From custom web applications to enterprise automation, we deliver software products that are clean, fast, and easy to maintain.',
  },
  {
    num: '04', badgeClass: 'sipl-bg-orange',
    title: 'Long-Term Partnership',
    desc: 'We stay on board as your technology partner, maintaining, optimizing, and scaling your software as your business grows.',
  },
];


const PROCESS_STEPS = [
  { num: '01', title: 'Discover', accentColor: '#1a2f6e', desc: 'We examine your business goals, user roles, and technical requirements.' },
  { num: '02', title: 'Scope & UX', accentColor: '#4caf50', desc: 'We design user flows and interactive prototypes for approval before coding.' },
  { num: '03', title: 'Develop', accentColor: '#0d9488', desc: 'We build your application in short, focused sprints with live weekly progress updates.' },
  { num: '04', title: 'Test & Launch', accentColor: '#f97316', desc: 'Rigorous speed, security, and device testing prior to production release.' },
  { num: '05', title: 'Support & Scale', accentColor: '#1a2f6e', desc: 'Proactive maintenance, feature updates, and cloud monitoring as you grow.' },
];



/* ─── Mouse Parallax Hook (Lightweight RAF, zero React re-renders) ─── */
const useMouseParallax = (strength = 10) => {
  const ref = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = ((e.clientX - cx) / rect.width) * strength;
        const dy = ((e.clientY - cy) / rect.height) * strength;
        ref.current.style.setProperty('--mp-x', `${dx.toFixed(2)}px`);
        ref.current.style.setProperty('--mp-y', `${dy.toFixed(2)}px`);
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [strength]);

  return { ref };
};

/* ─── SECTION 1: HERO ─── */
const AboutHero = () => {
  const { ref } = useMouseParallax(12);

  return (
    <section
      ref={ref}
      aria-labelledby="about-hero-heading"
      className="py-4 border-bottom sipl-section-transparent sipl-hero"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container-fluid px-3 px-sm-4 px-lg-5 py-3" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
        <div className="row align-items-center g-4 g-xl-5">

          {/* Left Column Text Content */}
          <div className="col-12 col-lg-5">
            <span
              className="text-uppercase fw-bold d-block mb-2 sipl-reveal-ttb"
              style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em', '--gr-delay': '120ms' }}
            >
              ABOUT SEIKURAL INNOVATION
            </span>

            <h1
              id="about-hero-heading"
              className="mb-3 sipl-reveal-ttb"
              style={{
                fontSize: 'clamp(1.8rem, 2.85vw, 2.45rem)',
                letterSpacing: '-0.025em',
                lineHeight: 1.22,
                color: '#0f172a',
                fontWeight: 600,
                '--gr-delay': '180ms',
              }}
            >
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>
                Technology Built Around
              </span>
              <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>
                Your Business
              </span>
            </h1>

            <div
              className="mb-4 sipl-reveal-ttb"
              style={{
                paddingLeft: '0',
                '--gr-delay': '320ms',
              }}
            >
              <p
                className="mb-0"
                style={{
                  fontSize: '0.96rem',
                  lineHeight: 1.68,
                  maxWidth: 540,
                  color: '#475569',
                }}
              >
                Seikural Innovation is a technology company focused on building custom software solutions for real business needs. We combine business understanding, modern engineering, and thoughtful design to create reliable digital products, streamline workflows, and help businesses operate more efficiently.
              </p>
            </div>

            <div
              className="d-flex flex-wrap align-items-center gap-3 mb-4 sipl-reveal-ttb"
              style={{ '--gr-delay': '460ms' }}
            >
              <CtaButton
                to="/services/web-development"
                variant="primary"
              >
                Explore Our Services
              </CtaButton>

              <CtaButton
                to="/contact"
                variant="secondary"
              >
                Start a Conversation
              </CtaButton>
            </div>

            <div
              className="sipl-reveal-ttb"
              style={{
                fontSize: '0.84rem',
                color: '#64748b',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                '--gr-delay': '600ms',
              }}
            >
              Custom Web Apps · Enterprise Systems · UI/UX Design · Cloud DevOps · Digital Marketing
            </div>
          </div>

          {/* Right Column Image Container (Prepared for image user will share) */}
          <div
            className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb"
            style={{ '--gr-delay': '350ms' }}
          >
            <div
              className="sipl-hero-slider-wrapper w-100 position-relative"
              style={{
                height: '440px',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={Images?.about}
                alt="Technology Built Around Your Business - Seikural Innovation"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                }}
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};


/* ─── SECTION 3: OUR STORY ─── */
const OurStory = () => (
  <section className="py-5 sipl-section-transparent" aria-label="Our Story" style={{ position: 'relative', zIndex: 1 }}>
    <div className="container py-4">
      <div className="row g-5">

        {/* Left — text */}
        <div className="col-12 col-lg-5 sipl-reveal-ltr">
          <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
            OUR STORY
          </span>
          <h2 className="display-6 fw-bold text-dark mb-4">
            Founded to make custom software straightforward.
          </h2>
          <p className="text-muted mb-3" style={{ lineHeight: '1.8', fontSize: '0.98rem' }}>
            Seikural Innovation was created with a clear purpose: to help businesses build digital tools designed around their actual operational routine.
          </p>
          <p className="text-muted mb-3" style={{ lineHeight: '1.8', fontSize: '0.98rem' }}>
            Too many companies waste time struggling with off-the-shelf software that doesn't fit their needs or managing complex agencies that speak only in tech jargon.
          </p>
          <p className="text-muted" style={{ lineHeight: '1.8', fontSize: '0.98rem' }}>
            We built a streamlined agency model—combining agile engineering, clear user experience design, and transparent communication so you can turn ideas into working software with total confidence.
          </p>
        </div>

        {/* Right — journey steps */}
        <div className="col-12 col-lg-6 offset-lg-1">
          <div className="d-flex flex-column gap-3">
            {STORY_STEPS.map((step, i) => (
              <div
                key={step.num}
                className="sipl-reveal-btt"
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="card border-0 shadow-sm rounded-4 p-4 sipl-card-hover" style={{ background: '#ffffff' }}>
                  <div className="d-flex align-items-start gap-3">
                    <span
                      className={`badge ${step.badgeClass} text-white fw-bold rounded px-2.5 py-1 flex-shrink-0`}
                      style={{ fontSize: '0.82rem', marginTop: 2 }}
                    >
                      {step.num}
                    </span>
                    <div>
                      <h3 className="h6 fw-bold text-dark mb-1">{step.title}</h3>
                      <p className="text-muted small mb-0" style={{ lineHeight: '1.65' }}>{step.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  </section>
);

/* ─── SECTION 4: MISSION & VISION ─── */
const MissionVision = () => (
  <section className="py-5 sipl-section-tint-translucent" aria-label="Our Purpose, Mission and Vision" style={{ position: 'relative', zIndex: 1 }}>
    <div className="container py-4">

      {/* Purpose statement */}
      <div className="row mb-5">
        <div className="col-12 col-lg-8 sipl-reveal-ttb">
          <span className="text-uppercase fw-bold sipl-text-teal small d-block mb-2" style={{ letterSpacing: '0.12em' }}>
            OUR PURPOSE
          </span>
          <p
            className="fw-bold text-dark mb-3"
            style={{ fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', lineHeight: 1.18, letterSpacing: '-0.025em' }}
          >
            Engineering software that works reliably.
          </p>
          <p className="text-muted" style={{ maxWidth: 580, lineHeight: '1.8', fontSize: '0.98rem' }}>
            Good technology should solve real problems, simplify daily work, and deliver a solid return on investment. Our purpose is to build software tools that businesses can count on every single day.
          </p>
        </div>
      </div>

      {/* Mission + Vision */}
      <div className="row g-4 align-items-stretch">

        {/* Mission */}
        <div className="col-12 col-lg-5 sipl-reveal-ltr">
          <div
            className="p-4 p-md-5 h-100 rounded-4 shadow-sm"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderTop: '4px solid #1a2f6e' }}
          >
            <span
              className="text-uppercase fw-bold sipl-text-navy small d-block mb-3"
              style={{ letterSpacing: '0.12em', fontSize: '0.72rem' }}
            >
              OUR MISSION
            </span>
            <h2
              className="fw-bold text-dark mb-0"
              style={{ fontSize: 'clamp(1.05rem, 1.9vw, 1.35rem)', lineHeight: 1.5, letterSpacing: '-0.01em', fontWeight: 800 }}
            >
              To design and deliver practical, high-performance digital products that help businesses automate tasks, serve customers better, and scale with confidence.
            </h2>
          </div>
        </div>

        {/* Divider */}
        <div className="col-lg-1 d-none d-lg-flex align-items-center justify-content-center sipl-reveal">
          <div style={{ width: 1, height: 100, background: 'linear-gradient(to bottom, transparent, #cbd5e1, transparent)' }} />
        </div>

        {/* Vision */}
        <div className="col-12 col-lg-5 offset-lg-0 sipl-reveal-rtl" style={{ transitionDelay: '0.14s' }}>
          <div
            className="p-4 p-md-5 h-100 rounded-4 shadow-sm"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderTop: '4px solid #0d9488' }}
          >
            <span
              className="text-uppercase fw-bold sipl-text-teal small d-block mb-3"
              style={{ letterSpacing: '0.12em', fontSize: '0.72rem' }}
            >
              OUR VISION
            </span>
            <h2
              className="fw-bold text-dark mb-0"
              style={{ fontSize: 'clamp(1.05rem, 1.9vw, 1.35rem)', lineHeight: 1.5, letterSpacing: '-0.01em', fontWeight: 800 }}
            >
              To become the most reliable technology engineering partner for growing companies by combining practical design, clean code, and transparent delivery.
            </h2>
          </div>
        </div>

      </div>
    </div>
  </section>
);



/* ─── SECTION 8: CLOSING CTA ─── */
const AboutCTA = () => (
  <section
    className="py-5 sipl-section-transparent text-center position-relative border-top"
    aria-label="Start a Conversation"
    style={{ position: 'relative', zIndex: 1 }}
  >
    <div className="sipl-color-bar position-absolute top-0 start-0" />
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 sipl-reveal-ttb">

          <span
            className="text-uppercase fw-bold sipl-text-navy small d-block mb-3"
            style={{ letterSpacing: '0.12em' }}
          >
            START A CONVERSATION
          </span>

          <h2 className="display-5 fw-bold text-dark mb-3">
            Have a project in mind or an operational challenge to solve?
          </h2>

          <p
            className="text-muted fs-6 mb-4"
            style={{ lineHeight: '1.8', maxWidth: '680px', margin: '0 auto 1.8rem auto' }}
          >
            Reach out for an informal, 15-minute consultation. We'll discuss your requirements, explain your tech options, and suggest a practical path forward—with no sales pressure.
          </p>

          <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3">
            <CtaButton to="/contact" variant="primary">
              Talk to Our Technical Team
            </CtaButton>
            <CtaButton to="/services/web-development" variant="secondary">
              View All Services
            </CtaButton>
          </div>

        </div>
      </div>
    </div>
  </section>
);

/* ─── MAIN EXPORT ─── */
const About = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'About Us | Seikural Innovation Pvt. Ltd.';
    return () => { document.title = prev; };
  }, []);

  useScrollReveal();

  return (
    <div className="sipl-home-page-wrapper">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(26,47,110,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '25%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(13,148,136,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '55%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(76,175,80,0.035) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '80%', right: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(249,115,22,0.025) 0%, transparent 70%)' }} />

      {/* Hero */}
      <AboutHero />


      {/* Our Story */}
      <OurStory />

      {/* Mission & Vision */}
      <MissionVision />


      {/* Final Call to Action */}
      <AboutCTA />
    </div>
  );
};

export default About;
